/*
 * Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
 * Distributed under the TruVideo proprietary license.
 */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Reorder, useDragControls } from 'framer-motion';
import { GripVertical, Edit2, Trash2, Check, X, MessageSquare } from 'lucide-react';
import { serviceRequestService } from '../../services/serviceRequestService';
import toastService from '../../utils/toastService';
import { formatRelativeTime } from '../../utils/formatRelativeTime';
import { SERVICE_REQUEST_CHECKLIST_ITEM_TITLE_MAX_LENGTH } from '../../constants/serviceRequests';
import ChecklistItemComments from './ChecklistItemComments';
import ChecklistIconTooltip from './ChecklistIconTooltip';
import ChecklistItemNumber from './ChecklistItemNumber';
import { apiErrorMessage, checklistPermissionsShape, formatDateTime } from './checklistHelpers';

/**
 * One draggable checklist item. Dragging starts only from the grip handle
 * (dragListener is off), so clicks on the checkbox, title and buttons work normally.
 */
const ChecklistItemRow = ({
  item,
  number,
  serviceRequestUid,
  permissions,
  onUpdated,
  onDelete,
  onCommentCountChange,
  onChanged,
  onDragStart,
  onDragEnd,
}) => {
  const dragControls = useDragControls();
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState(false);

  const handleToggle = async () => {
    try {
      setToggling(true);
      const { data } = await serviceRequestService.updateChecklistItem(
        serviceRequestUid, item.checklistUid, item.uid, { isCompleted: !item.isCompleted },
      );
      onUpdated(data);
      onChanged();
    } catch (err) {
      toastService.error(apiErrorMessage(err, 'Failed to update item'));
    } finally {
      setToggling(false);
    }
  };

  const startEdit = () => {
    setTitleDraft(item.title);
    setEditing(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const title = titleDraft.trim();
    if (!title) return;
    if (title === item.title) {
      setEditing(false);
      return;
    }
    try {
      setSaving(true);
      const { data } = await serviceRequestService.updateChecklistItem(
        serviceRequestUid, item.checklistUid, item.uid, { title },
      );
      onUpdated(data);
      setEditing(false);
    } catch (err) {
      toastService.error(apiErrorMessage(err, 'Failed to update item'));
    } finally {
      setSaving(false);
    }
  };

  const completedBy = item.completedByName || 'Unknown user';

  return (
    <Reorder.Item
      as="div"
      value={item}
      dragListener={false}
      dragControls={dragControls}
      onDragEnd={onDragEnd}
      className="relative bg-base-100 rounded-xl"
      whileDrag={{ scale: 1.01, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 10 }}
    >
      <div className="group flex items-start gap-2 p-2 rounded-xl border border-base-content/5 hover:bg-base-200/50 transition-colors">
        {permissions.itemReorder && (
          <ChecklistIconTooltip tip="Drag to reorder">
            <button
              type="button"
              aria-label="Drag to reorder"
              onPointerDown={(e) => {
                onDragStart();
                dragControls.start(e);
              }}
              style={{ touchAction: 'none' }}
              className="mt-0.5 p-0.5 text-base-content/30 hover:text-base-content/70 cursor-grab active:cursor-grabbing"
            >
              <GripVertical size={16} />
            </button>
          </ChecklistIconTooltip>
        )}

        <ChecklistItemNumber number={number} className="mt-0.5 shrink-0" />

        {permissions.itemStatus ? (
          <ChecklistIconTooltip tip={item.isCompleted ? 'Mark as not done' : 'Mark as done'}>
            <input
              type="checkbox"
              checked={item.isCompleted}
              onChange={handleToggle}
              disabled={toggling}
              className="checkbox checkbox-sm checkbox-primary mt-0.5 rounded-md"
              aria-label={item.isCompleted ? 'Mark as not done' : 'Mark as done'}
            />
          </ChecklistIconTooltip>
        ) : (
          <input
            type="checkbox"
            checked={item.isCompleted}
            disabled
            readOnly
            className="checkbox checkbox-sm checkbox-primary mt-0.5 rounded-md"
            aria-label={item.isCompleted ? 'Done' : 'Not done'}
          />
        )}

        <div className="flex-1 min-w-0">
          {editing ? (
            <form onSubmit={handleSave} className="flex items-center gap-1.5">
              <input
                type="text"
                value={titleDraft}
                onChange={(e) => setTitleDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') setEditing(false);
                }}
                maxLength={SERVICE_REQUEST_CHECKLIST_ITEM_TITLE_MAX_LENGTH}
                aria-label="Checklist item title"
                autoFocus
                disabled={saving}
                className="input input-sm input-bordered flex-1 min-w-0 rounded-lg text-sm focus:outline-none focus:border-primary"
              />
              <ChecklistIconTooltip tip="Save">
                <button type="submit" aria-label="Save" disabled={saving || !titleDraft.trim()} className="btn btn-xs btn-primary btn-square rounded-lg">
                  {saving ? <span className="loading loading-spinner loading-xs"></span> : <Check size={12} />}
                </button>
              </ChecklistIconTooltip>
              <ChecklistIconTooltip tip="Cancel">
                <button type="button" aria-label="Cancel" onClick={() => setEditing(false)} disabled={saving} className="btn btn-xs btn-ghost btn-square rounded-lg">
                  <X size={12} />
                </button>
              </ChecklistIconTooltip>
            </form>
          ) : (
            <p className={`text-sm leading-snug break-words ${item.isCompleted ? 'line-through text-base-content/40' : 'text-base-content'}`}>
              {item.title}
            </p>
          )}
          {item.isCompleted && item.completedAt && !editing && (
            <p className="text-[11px] text-base-content/40 mt-0.5" title={formatDateTime(item.completedAt)}>
              Done by {completedBy} · {formatRelativeTime(item.completedAt)}
            </p>
          )}
        </div>

        {!editing && (
          <div className="flex items-center gap-0.5 shrink-0">
            <ChecklistIconTooltip tip={expanded ? 'Hide comments' : 'Comments'}>
              <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                aria-label={expanded ? 'Hide comments' : 'Show comments'}
                aria-expanded={expanded}
                className={`inline-flex items-center gap-1 px-1.5 py-1 rounded-lg text-[11px] font-bold transition-all ${expanded ? 'text-primary bg-primary/10' : 'text-base-content/40 hover:text-primary hover:bg-primary/10'}`}
              >
                <MessageSquare size={13} />
                {item.commentCount > 0 && <span>{item.commentCount}</span>}
              </button>
            </ChecklistIconTooltip>
            {permissions.itemEdit && (
              <ChecklistIconTooltip tip="Edit">
                <button
                  type="button"
                  onClick={startEdit}
                  aria-label="Edit item"
                  className="p-1.5 text-base-content/40 hover:text-primary hover:bg-primary/10 rounded-lg transition-all"
                >
                  <Edit2 size={13} />
                </button>
              </ChecklistIconTooltip>
            )}
            {permissions.itemDelete && (
              <ChecklistIconTooltip tip="Delete" variant="error" position="left">
                <button
                  type="button"
                  onClick={() => onDelete(item)}
                  aria-label="Delete item"
                  className="p-1.5 text-base-content/40 hover:text-error hover:bg-error/10 rounded-lg transition-all"
                >
                  <Trash2 size={13} />
                </button>
              </ChecklistIconTooltip>
            )}
          </div>
        )}
      </div>

      {expanded && (
        <ChecklistItemComments
          serviceRequestUid={serviceRequestUid}
          checklistUid={item.checklistUid}
          itemUid={item.uid}
          permissions={permissions}
          onCountChange={(delta) => onCommentCountChange(item.uid, delta)}
          onChanged={onChanged}
        />
      )}
    </Reorder.Item>
  );
};

ChecklistItemRow.propTypes = {
  item: PropTypes.shape({
    uid: PropTypes.string.isRequired,
    checklistUid: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    isCompleted: PropTypes.bool,
    completedAt: PropTypes.string,
    completedByName: PropTypes.string,
    commentCount: PropTypes.number,
  }).isRequired,
  number: PropTypes.number.isRequired,
  serviceRequestUid: PropTypes.string.isRequired,
  permissions: checklistPermissionsShape.isRequired,
  onUpdated: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onCommentCountChange: PropTypes.func.isRequired,
  onChanged: PropTypes.func.isRequired,
  onDragStart: PropTypes.func.isRequired,
  onDragEnd: PropTypes.func.isRequired,
};

export default ChecklistItemRow;
